import { getActiveEpicState, getLaunchMode } from '@/features/auth/launchContext'
import { EPIC_FHIR_SESSIONS_UPDATED_EVENT, EpicAuthClient } from '@/features/auth/services/EpicAuthClient'
import { useCallback, useEffect, useState } from 'react'

/** How often the hook re-evaluates the Epic access-token expiry. */
const EPIC_EXPIRY_POLL_INTERVAL_MS = 30 * 1000

export interface EpicSessionExpiryState {
  /**
   * True only for an active Epic (SMART on FHIR) launch whose access token has
   * passed its expiry (minus buffer). Always false for standalone Auth0
   * sessions, which manage their own lifetimes.
   */
  isExpired: boolean
  /**
   * True when the session is inside the pre-lockout warning window (approaching
   * expiry but not yet locked out). Mutually exclusive with `isExpired`.
   */
  isExpiryWarning: boolean
  /** Epoch millis when the current Epic session expires, or null if unknown / not an Epic launch. */
  expiresAt: number | null
  /** Epoch millis when the session locks out (expiry minus buffer), or null if unknown / not an Epic launch. */
  lockoutAt: number | null
}

const NON_EPIC_STATE: EpicSessionExpiryState = {
  isExpired: false,
  isExpiryWarning: false,
  expiresAt: null,
  lockoutAt: null,
}

/**
 * Evaluate the current Epic launch session's expiry once.
 *
 * Safe to call outside React and never throws (EpicAuthClient construction can
 * fail if env vars are missing); on any failure it reports a non-expired,
 * unknown-expiry state so we never lock out a session we cannot reason about.
 */
export function evaluateEpicSessionExpiry(now: number = Date.now()): EpicSessionExpiryState {
  try {
    if (getLaunchMode() !== 'epic_smart') {
      return NON_EPIC_STATE
    }
    const state = getActiveEpicState()
    if (!state) {
      return NON_EPIC_STATE
    }
    const client = EpicAuthClient.getInstance()
    return {
      isExpired: client.isAccessTokenExpired(state, now),
      isExpiryWarning: client.isAccessTokenExpiryWarning(state, now),
      expiresAt: client.getAccessTokenExpiresAt(state),
      lockoutAt: client.getAccessTokenLockoutAt(state),
    }
  } catch {
    return NON_EPIC_STATE
  }
}

/**
 * React hook that tracks whether the active Epic-launched session's access token
 * has expired (with buffer). Re-evaluates on an interval, on tab focus, and when
 * EpicAuthClient writes new session data.
 *
 * Returns `{ isExpired: false }` for standalone / non-Epic sessions.
 */
export function useEpicSessionExpiry(): EpicSessionExpiryState {
  const [expiryState, setExpiryState] = useState<EpicSessionExpiryState>(() => evaluateEpicSessionExpiry())

  const reevaluate = useCallback(() => {
    setExpiryState((prev) => {
      const next = evaluateEpicSessionExpiry()
      // Avoid re-rendering when nothing changed.
      if (
        next.isExpired === prev.isExpired &&
        next.isExpiryWarning === prev.isExpiryWarning &&
        next.expiresAt === prev.expiresAt &&
        next.lockoutAt === prev.lockoutAt
      ) {
        return prev
      }
      return next
    })
  }, [])

  useEffect(() => {
    // Re-evaluate immediately in case the session changed between the initial
    // lazy state and effect setup.
    reevaluate()

    const interval = window.setInterval(reevaluate, EPIC_EXPIRY_POLL_INTERVAL_MS)

    const onFocus = () => reevaluate()
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        reevaluate()
      }
    }

    window.addEventListener('focus', onFocus)
    window.addEventListener(EPIC_FHIR_SESSIONS_UPDATED_EVENT, reevaluate)
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      window.clearInterval(interval)
      window.removeEventListener('focus', onFocus)
      window.removeEventListener(EPIC_FHIR_SESSIONS_UPDATED_EVENT, reevaluate)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [reevaluate])

  return expiryState
}
